import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

import { API, BACKEND_URL } from '../utils/apiBase';

/**
 * Realtime comms hook for manager <-> judges.
 *   role: "manager" | "judge"
 *   judgeNumber: required when role==='judge'
 * Returns:
 *   - latestMessage      (judge)  : the most recent manager message OR null
 *   - dismissMessage     (judge)  : clear latestMessage locally (popup close)
 *   - helpStates         (manager): { [judge_number]: true } for active HELP
 *   - lastReaction       (manager): { judge_number, reaction_type } new reactions
 *   - sendMessage        (manager): (message, judgeNumber?) -> Promise
 *   - sendReaction       (judge)  : (type, messageId?) -> Promise
 *   - triggerHelp        (judge)  : () -> Promise
 *   - resolveHelp        (manager): (judgeNumber) -> Promise
 *   - connected: bool
 */
export default function useComms(eventId, role, judgeNumber = null) {
  const [latestMessage, setLatestMessage] = useState(null);
  const [helpStates, setHelpStates] = useState({});
  const [lastReaction, setLastReaction] = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const closedRef = useRef(false);

  // Build WS URL
  const wsUrl = (() => {
    if (!eventId) return null;
    // Same-origin when possible — the ONLY thing that works on custom
    // domains (e.g. judging.worldfreestylefootball.org). See useTimerWS
    // for the full explanation. Falls back to REACT_APP_BACKEND_URL in
    // non-browser environments.
    let host, wsScheme;
    if (typeof window !== "undefined" && window.location) {
      host = window.location.host;
      wsScheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    } else if (BACKEND_URL) {
      const u = new URL(BACKEND_URL);
      host = u.host;
      wsScheme = u.protocol === "https:" ? "wss:" : "ws:";
    } else {
      return null;
    }
    const base = `${wsScheme}//${host}/api/comms/ws/${encodeURIComponent(eventId)}`;
    const qs = role === "judge"
      ? `?role=judge&judge_number=${encodeURIComponent(judgeNumber)}`
      : `?role=manager`;
    return base + qs;
  })();

  // Fetch initial state on mount (REST fallback for HELP states)
  const refreshState = useCallback(async () => {
    if (!eventId || role !== "manager") return;
    try {
      const res = await axios.get(`${API}/comms/state?event_id=${eventId}`);
      const states = {};
      for (const s of res.data?.help_states || []) {
        states[s.judge_number] = true;
      }
      setHelpStates(states);
    } catch {
      /* ignore */
    }
  }, [eventId, role]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  // WebSocket connection with auto-reconnect
  useEffect(() => {
    if (!wsUrl) return undefined;
    closedRef.current = false;
    let retryTimer = null;
    let pingTimer = null;

    const open = () => {
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        retryTimer = setTimeout(open, 3000);
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        pingTimer = setInterval(() => {
          try { ws.send("ping"); } catch { /* ignore */ }
        }, 20000);
      };
      ws.onmessage = (e) => {
        let msg = null;
        try { msg = JSON.parse(e.data); } catch { return; }
        switch (msg.type) {
          case "manager_message":
            // judge side: show popup (replaces any current)
            if (role === "judge") setLatestMessage(msg);
            break;
          case "reaction":
            // manager side: toast
            if (role === "manager") {
              setLastReaction({ judge_number: msg.judge_number, reaction_type: msg.reaction_type, ts: Date.now() });
            }
            break;
          case "help_triggered":
            if (role === "manager") {
              setHelpStates((prev) => ({ ...prev, [msg.judge_number]: true }));
              setLastReaction({ judge_number: msg.judge_number, reaction_type: "help", ts: Date.now() });
            }
            break;
          case "help_resolved":
            if (role === "manager") {
              setHelpStates((prev) => {
                const next = { ...prev };
                delete next[msg.judge_number];
                return next;
              });
            }
            break;
          case "snapshot":
            if (role === "manager" && Array.isArray(msg.help_states)) {
              const states = {};
              for (const s of msg.help_states) states[s.judge_number] = true;
              setHelpStates(states);
            }
            break;
          default:
            break;
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        if (!closedRef.current) {
          retryTimer = setTimeout(open, 3000);
        }
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };
    open();
    return () => {
      closedRef.current = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current && wsRef.current.close(); } catch { /* ignore */ }
    };
  }, [wsUrl, role]);

  // Public API
  const dismissMessage = useCallback(() => setLatestMessage(null), []);
  const clearLastReaction = useCallback(() => setLastReaction(null), []);

  const sendMessage = useCallback(async (message, jNum = null) => {
    return axios.post(`${API}/comms/message`, {
      event_id: eventId,
      target_type: jNum ? "judge" : "all",
      target_judge_number: jNum,
      message,
    });
  }, [eventId]);

  const sendReaction = useCallback(async (type, messageId = null) => {
    return axios.post(`${API}/comms/reaction`, {
      event_id: eventId,
      judge_number: judgeNumber,
      message_id: messageId,
      type,
    });
  }, [eventId, judgeNumber]);

  const triggerHelp = useCallback(async () => {
    return axios.post(`${API}/comms/help/trigger`, {
      event_id: eventId,
      judge_number: judgeNumber,
    });
  }, [eventId, judgeNumber]);

  const resolveHelp = useCallback(async (jNum) => {
    return axios.post(`${API}/comms/help/resolve`, {
      event_id: eventId,
      judge_number: jNum,
    });
  }, [eventId]);

  return {
    latestMessage,
    dismissMessage,
    helpStates,
    lastReaction,
    clearLastReaction,
    connected,
    sendMessage,
    sendReaction,
    triggerHelp,
    resolveHelp,
  };
}
